import { createTheme } from '@mui/material/styles';

// Earthy color palette inspired by classical Indian arts
const theme = createTheme({
  // Responsive breakpoints
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
  palette: {
    primary: {
      main: '#8B4513', // Saddle brown - earthy, classical
      light: '#A0653F',
      dark: '#6B3410',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#D4AF37', // Gold - for accents
      light: '#E5C158',
      dark: '#B8941F',
      contrastText: '#000000',
    },
    background: {
      default: '#FAF7F2', // Warm off-white
      paper: '#FFFFFF',
    },
    text: {
      primary: '#2C2416', // Dark brown
      secondary: '#5D4E37', // Medium brown
    },
    error: {
      main: '#C84B31',
    },
    success: {
      main: '#4A7C59',
    },
    info: {
      main: '#5F7A89',
    },
    warning: {
      main: '#E8A838',
    },
  },
  typography: {
    fontFamily: '"Lato", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Cormorant Garamond", serif',
      fontWeight: 600,
      fontSize: 'clamp(1.75rem, 4vw, 2.5rem)', // Responsive
      letterSpacing: '0.02em',
    },
    h2: {
      fontFamily: '"Cormorant Garamond", serif',
      fontWeight: 600,
      fontSize: 'clamp(1.5rem, 3.5vw, 2rem)', // Responsive
      letterSpacing: '0.02em',
    },
    h3: {
      fontFamily: '"Cormorant Garamond", serif',
      fontWeight: 600,
      fontSize: 'clamp(1.35rem, 3vw, 1.75rem)', // Responsive
      letterSpacing: '0.02em',
    },
    h4: {
      fontFamily: '"Cormorant Garamond", serif',
      fontWeight: 600,
      fontSize: 'clamp(1.2rem, 2.5vw, 1.5rem)', // Responsive
      letterSpacing: '0.01em',
    },
    h5: {
      fontFamily: '"Cormorant Garamond", serif',
      fontWeight: 600,
      fontSize: 'clamp(1.1rem, 2vw, 1.25rem)', // Responsive
      letterSpacing: '0.01em',
    },
    h6: {
      fontFamily: '"Cormorant Garamond", serif',
      fontWeight: 600,
      fontSize: 'clamp(1rem, 1.8vw, 1.1rem)', // Responsive
      letterSpacing: '0.01em',
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
      fontSize: 'clamp(0.875rem, 2vw, 0.95rem)', // Responsive
    },
    body1: {
      fontSize: 'clamp(0.875rem, 2vw, 1rem)', // Responsive
    },
    body2: {
      fontSize: 'clamp(0.8125rem, 1.8vw, 0.875rem)', // Responsive
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          // Smooth scrolling
          scrollBehavior: 'smooth',
        },
        // Ensure all containers respect viewport width
        '*': {
          boxSizing: 'border-box',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '10px 20px',
          fontSize: 'clamp(0.875rem, 2vw, 0.95rem)',
          fontWeight: 500,
          // Mobile: smaller padding
          '@media (max-width: 600px)': {
            padding: '8px 16px',
            fontSize: '0.875rem',
          },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(139, 69, 19, 0.2)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          transition: 'box-shadow 0.3s ease',
          '&:hover': {
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
          },
          // Mobile: reduce border radius
          '@media (max-width: 600px)': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '20px',
          // Mobile: reduce padding
          '@media (max-width: 600px)': {
            padding: '12px',
            '&:last-child': {
              paddingBottom: '12px',
            },
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            // Mobile: prevent iOS zoom on focus
            '@media (max-width: 600px)': {
              fontSize: '16px',
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          // Mobile: reduce border radius
          '@media (max-width: 600px)': {
            borderRadius: 8,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          // Mobile: smaller chip
          '@media (max-width: 600px)': {
            height: 24,
            fontSize: '0.75rem',
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          // Enable horizontal scroll on mobile for tables
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          '&::-webkit-scrollbar': {
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#8B4513',
            borderRadius: 4,
            '&:hover': {
              background: '#6B3410',
            },
          },
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          // Mobile: minimum width to enable horizontal scroll
          minWidth: 650,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '16px',
          // Mobile: reduce cell padding
          '@media (max-width: 600px)': {
            padding: '8px 4px',
            fontSize: '0.8125rem',
          },
        },
        head: {
          fontWeight: 600,
          '@media (max-width: 600px)': {
            fontSize: '0.75rem',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          // Mobile: full screen or near full screen
          '@media (max-width: 600px)': {
            margin: 16,
            maxWidth: 'calc(100% - 32px)',
            borderRadius: 8,
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: '20px 24px',
          '@media (max-width: 600px)': {
            padding: '16px',
            fontSize: '1.25rem',
          },
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '20px 24px',
          '@media (max-width: 600px)': {
            padding: '12px 16px',
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '16px 24px',
          '@media (max-width: 600px)': {
            padding: '12px 16px',
            flexDirection: 'column',
            '& > *': {
              width: '100%',
              margin: '4px 0',
            },
          },
        },
      },
    },
    MuiContainer: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            paddingLeft: 16,
            paddingRight: 16,
          },
        },
      },
    },
    MuiGrid: {
      styleOverrides: {
        root: {
          // Prevent grid overflow and negative margins
          width: '100%',
          marginLeft: 0,
          marginRight: 0,
        },
        container: {
          width: '100%',
          marginLeft: '0 !important',
          marginRight: '0 !important',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          '@media (max-width: 600px)': {
            '& .MuiToolbar-root': {
              minHeight: 56,
              paddingLeft: 12,
              paddingRight: 12,
            },
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          '@media (max-width: 960px)': {
            width: 240, // Narrower drawer on tablets/mobile
          },
        },
      },
    },
  },
});

export default theme;
